<template>
  <div class="overview-tab">
    <!-- 상단: 이미지 + 기본정보 -->
    <div class="overview-header">
      <!-- 이미지 영역 -->
      <div class="image-section">
        <div v-if="images.length > 0" class="image-gallery">
          <el-image
            v-for="(img, idx) in images"
            :key="idx"
            :src="img.url"
            :preview-src-list="images.map(i => i.url)"
            :initial-index="idx"
            fit="contain"
            class="product-image"
          >
            <template #error>
              <div class="image-placeholder">
                <el-icon :size="40"><Picture /></el-icon>
                <span>{{ img.label }}</span>
              </div>
            </template>
          </el-image>
        </div>
        <div v-else class="no-image">
          <el-icon :size="60" color="#dcdfe6"><Picture /></el-icon>
          <p>등록된 이미지가 없습니다</p>
        </div>

        <!-- 작업지시 이미지 업로드 -->
        <el-upload
          v-if="canEdit"
          action="#"
          :http-request="uploadInstructionImage"
          :show-file-list="false"
          :before-upload="beforeUpload"
          accept="image/*"
          class="instruction-upload"
        >
          <el-button size="small" type="primary" link>
            <el-icon><Upload /></el-icon>
            작업지시 이미지 업로드
          </el-button>
        </el-upload>
      </div>

      <!-- 기본정보 영역 -->
      <div class="info-section">
        <el-descriptions :column="2" border size="default">
          <el-descriptions-item label="스타일번호" :span="2">
            <span class="style-number">{{ mainRequest?.design_style_number }}</span>
            <el-tag size="small" type="info" class="internal-number">
              내부: {{ mainRequest?.design_internal_number }}
            </el-tag>
          </el-descriptions-item>

          <el-descriptions-item label="제품명">
            {{ mainRequest?.design_product_name || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="브랜드">
            {{ mainRequest?.brand_name }}
          </el-descriptions-item>

          <el-descriptions-item label="시즌">
            {{ mainRequest?.design_year || '' }} {{ mainRequest?.design_season || '' }}
          </el-descriptions-item>
          <el-descriptions-item label="난이도">
            <span v-if="mainRequest?.design_difficulty">
              {{ mainRequest.design_difficulty }}
            </span>
            <span v-else>-</span>
          </el-descriptions-item>

          <el-descriptions-item label="제조공장">
            {{ mainRequest?.manufacturer_name || '미정' }}
          </el-descriptions-item>
          <el-descriptions-item label="견적번호">
            <el-link
              v-if="mainRequest?.quotation"
              type="primary"
              :href="`/quotations/${mainRequest.quotation}`"
            >
              {{ mainRequest.quotation_number }}
            </el-link>
            <span v-else>-</span>
          </el-descriptions-item>

          <el-descriptions-item label="요청일">
            {{ mainRequest?.request_date }}
          </el-descriptions-item>
          <el-descriptions-item label="납기일">
            {{ mainRequest?.delivery_date }}
            <el-tag
              :type="getDdayType(mainRequest?.days_until_delivery)"
              size="small"
              style="margin-left: 6px"
            >
              {{ formatDday(mainRequest?.days_until_delivery) }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <!-- 혼용율 / 가공방법 -->
        <div class="spec-section">
          <div class="spec-row">
            <label>혼용율</label>
            <span v-if="!editing" class="spec-value" @click="startEdit">
              {{ mainRequest?.mix_ratio || '(미입력)' }}
            </span>
            <el-input v-else v-model="editForm.mix_ratio" size="small" placeholder="예: 면 95% 폴리 5%" />
          </div>
          <div class="spec-row">
            <label>가공방법</label>
            <span v-if="!editing" class="spec-value" @click="startEdit">
              {{ mainRequest?.process_method || '(미입력)' }}
            </span>
            <el-input v-else v-model="editForm.process_method" type="textarea" :rows="2" size="small" placeholder="예: 워싱 후 텀블 건조" />
          </div>
          <div class="spec-row">
            <label>비고</label>
            <span v-if="!editing" class="spec-value" @click="startEdit">
              {{ mainRequest?.remarks || '(미입력)' }}
            </span>
            <el-input v-else v-model="editForm.remarks" type="textarea" :rows="2" size="small" />
          </div>
          <div v-if="editing && canEdit" class="spec-actions">
            <el-button size="small" @click="cancelEdit">취소</el-button>
            <el-button size="small" type="primary" @click="saveEdit" :loading="saving">저장</el-button>
          </div>
          <el-button v-if="!editing && canEdit" size="small" link type="primary" @click="startEdit">
            <el-icon><Edit /></el-icon> 편집
          </el-button>
        </div>
      </div>
    </div>

    <!-- 수량 요약 -->
    <div class="quantity-summary">
      <h4>수량 요약</h4>
      <div class="quantity-info-row">
        <div class="qty-item">
          <span class="qty-label">발주수량</span>
          <span class="qty-value">{{ formatNumber(mainRequest?.order_quantity) }} <small>PCS</small></span>
        </div>
        <div v-if="mainRequest?.allowance_rate > 0" class="qty-item">
          <span class="qty-label">여유율</span>
          <span class="qty-value allowance">+{{ mainRequest.allowance_rate }}%</span>
        </div>
        <div class="qty-item">
          <span class="qty-label">생산수량</span>
          <span class="qty-value production">{{ formatNumber(mainRequest?.production_quantity) }} <small>PCS</small></span>
        </div>
        <div v-if="mainRequest?.allowance_rate > 0" class="qty-item qty-extra">
          <span class="qty-label">여유분</span>
          <span class="qty-value">{{ formatNumber((mainRequest?.production_quantity || 0) - (mainRequest?.order_quantity || 0)) }} <small>PCS</small></span>
        </div>
      </div>
      <el-table
        v-if="mainRequest?.colors?.length > 0"
        :data="colorsWithTotal"
        size="small"
        :show-header="true"
        border
        class="color-summary-table"
      >
        <el-table-column prop="color_name" label="컬러" />
        <el-table-column prop="color_code" label="코드" width="100" />
        <el-table-column prop="quantity" label="수량" width="120" align="right">
          <template #default="{ row }">
            <strong v-if="row._isTotal">{{ formatNumber(row.quantity) }}</strong>
            <span v-else>{{ formatNumber(row.quantity) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-else description="등록된 컬러 정보가 없습니다" :image-size="60" />
    </div>

    <!-- 담당자 요약 -->
    <div class="participant-summary">
      <h4>담당자</h4>
      <div v-if="participants?.length > 0" class="participant-chips">
        <el-tag
          v-for="p in participants"
          :key="p.id"
          :type="getRoleTagType(p.role)"
          size="default"
          class="participant-chip"
        >
          {{ p.role_display }}: {{ p.employee_name }}
        </el-tag>
      </div>
      <el-empty v-else description="배정된 담당자가 없습니다" :image-size="60" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Picture, Upload, Edit } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  mainRequest: { type: Object, default: null },
  participants: { type: Array, default: () => [] },
  canEdit: { type: Boolean, default: false },
})

const emit = defineEmits(['updated'])

const editing = ref(false)
const saving = ref(false)
const editForm = ref({ mix_ratio: '', process_method: '', remarks: '' })

const images = computed(() => {
  const imgs = []
  if (props.mainRequest?.instruction_image) {
    imgs.push({ url: props.mainRequest.instruction_image, label: '메인오더' })
  }
  if (props.mainRequest?.design_front_image) {
    imgs.push({ url: props.mainRequest.design_front_image, label: '정면' })
  }
  if (props.mainRequest?.design_back_image) {
    imgs.push({ url: props.mainRequest.design_back_image, label: '후면' })
  }
  return imgs
})

const colorsWithTotal = computed(() => {
  const colors = props.mainRequest?.colors || []
  if (colors.length === 0) return []
  const total = colors.reduce((sum, c) => sum + (c.quantity || 0), 0)
  return [
    ...colors,
    { color_name: '합계', color_code: '', quantity: total, _isTotal: true }
  ]
})

const getDdayType = (days) => {
  if (days === null || days === undefined) return 'info'
  if (days < 0) return 'danger'
  if (days <= 7) return 'warning'
  return 'info'
}

const formatDday = (days) => {
  if (days === null || days === undefined) return '-'
  if (days < 0) return `D+${Math.abs(days)}`
  return `D-${days}`
}

const formatNumber = (num) => num?.toLocaleString() || '0'

const getRoleTagType = (role) => {
  const types = { MD: '', MATERIAL: 'success', PRODUCTION: 'warning', TRADE: 'info', QC: 'danger' }
  return types[role] || 'info'
}

const startEdit = () => {
  if (!props.canEdit) return
  editForm.value = {
    mix_ratio: props.mainRequest?.mix_ratio || '',
    process_method: props.mainRequest?.process_method || '',
    remarks: props.mainRequest?.remarks || '',
  }
  editing.value = true
}

const cancelEdit = () => {
  editing.value = false
}

const saveEdit = async () => {
  saving.value = true
  try {
    await axiosInstance.patch(
      `/production/main-requests/${props.mainRequest.id}/`,
      editForm.value
    )
    ElMessage.success('저장되었습니다.')
    editing.value = false
    emit('updated')
  } catch {
    ElMessage.error('저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

const beforeUpload = (file) => {
  const isImage = file.type.startsWith('image/')
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isImage) { ElMessage.error('이미지 파일만 업로드 가능합니다.'); return false }
  if (!isLt10M) { ElMessage.error('10MB 이하 파일만 업로드 가능합니다.'); return false }
  return true
}

const uploadInstructionImage = async ({ file }) => {
  const formData = new FormData()
  formData.append('instruction_image', file)
  try {
    await axiosInstance.patch(
      `/production/main-requests/${props.mainRequest.id}/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    ElMessage.success('이미지가 업로드되었습니다.')
    emit('updated')
  } catch {
    ElMessage.error('이미지 업로드에 실패했습니다.')
  }
}
</script>

<style scoped>
.overview-tab {
  padding: 20px;
}

.overview-header {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}

.image-section {
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.image-gallery {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-image {
  width: 280px;
  height: 200px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  gap: 8px;
}

.no-image {
  width: 280px;
  height: 200px;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  gap: 8px;
}

.instruction-upload {
  text-align: center;
}

.info-section {
  flex: 1;
  min-width: 0;
}

.style-number {
  font-size: 16px;
  font-weight: bold;
  margin-right: 10px;
}

.internal-number {
  font-size: 11px;
}

.spec-section {
  margin-top: 16px;
  padding: 16px;
  background: #fafafa;
  border-radius: 6px;
}

.spec-row {
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
  align-items: flex-start;
}

.spec-row label {
  flex: 0 0 70px;
  font-weight: 600;
  font-size: 13px;
  color: #606266;
  padding-top: 4px;
}

.spec-value {
  flex: 1;
  color: #303133;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  min-height: 24px;
}

.spec-value:hover {
  background: #ecf5ff;
}

.spec-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.quantity-summary {
  margin-bottom: 24px;
}

.quantity-summary h4,
.participant-summary h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #303133;
  border-left: 3px solid #409eff;
  padding-left: 8px;
}

.quantity-info-row {
  display: flex;
  gap: 24px;
  margin-bottom: 14px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  align-items: center;
}

.qty-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.qty-label {
  font-size: 12px;
  color: #909399;
}

.qty-value {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}

.qty-value small {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}

.qty-value.allowance {
  color: #e6a23c;
}

.qty-value.production {
  color: #409eff;
}

.qty-extra {
  border-left: 1px solid #dcdfe6;
  padding-left: 24px;
}

.color-summary-table {
  max-width: 500px;
}

.participant-summary {
  margin-bottom: 16px;
}

.participant-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.participant-chip {
  font-size: 13px;
}

@media (max-width: 768px) {
  .overview-header {
    flex-direction: column;
  }
  .image-section {
    flex: none;
  }
  .product-image, .no-image {
    width: 100%;
  }
}
</style>
